import { useI18n, type Locale } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A NAME, IN THE READER'S OWN ORDER — item 6.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's ruling of 9 Sep 2026: Hungarian writes the family name first (`Kovács Anna`)
 * and English writes it last (`Anna Kovács`), from the same two stored fields. One helper, so the
 * order is decided once rather than at each of the places a name is drawn.
 *
 * ⚠️⚠️ **`displayName` WHEN EITHER HALF IS MISSING, AND THAT IS THE LOAD-BEARING BRANCH.** `0101`
 * backfilled the halves by splitting at the first space, which is a guess about somebody's name —
 * wrong for a two-word family name, for a name with no space in it, and for one typed the other
 * way round. A row we could not split has neither half, and this renders exactly what was
 * there before. Nobody's name is silently reordered on the strength of a guess.
 *
 * ⚠️ **An invitation row has no name at all** (item 44), and its `displayName` is the email
 * address — which is the right thing to show and arrives here through the same branch.
 *
 * ⚠️ **NOT `Intl.DisplayNames` or a locale-aware name formatter**: neither exists for personal
 * names in any engine we ship to, and the two orders are the whole of what this product needs.
 */
export function personName(
  person: {
    readonly displayName: string;
    readonly givenName?: string | null;
    readonly familyName?: string | null;
  },
  locale: Locale,
): string {
  const given = person.givenName?.trim() ?? '';
  const family = person.familyName?.trim() ?? '';
  if (given === '' || family === '') return person.displayName;

  return locale === 'hu' ? `${family} ${given}` : `${given} ${family}`;
}

/**
 * The same thing, for a component: the locale comes from the provider rather than from a prop
 * threaded through four surfaces.
 *
 * ⚠️ Returns the FUNCTION, not a name — a list draws a hundred of them and a hook cannot be called
 * per row.
 */
export function usePersonName(): (person: {
  readonly displayName: string;
  readonly givenName?: string | null;
  readonly familyName?: string | null;
}) => string {
  const { locale } = useI18n();
  return (person) => personName(person, locale);
}
